import { TypeformForm } from '@/api/model/typeform/typeform-form'
import { TypeformWelcomeScreen } from '@/api/model/typeform/typeform-welcome-screen'
import { TypeformThankYouScreen } from '@/api/model/typeform/typeform-thank-you-screen'
import { TypeformLogicJumpFlattenedCondition } from '@/api/model/typeform/typeform-logic-jump-flattened-condition'
import { TypeformMap } from '@/api/model/typeform/typeform-map'
import { TypeformReferenceRegExp } from '@/api/model/typeform/typeform-form'

export class TypeformField {
    constructor(
        public id: string,
        public ref: string,
        public title: string,
        public _type: string,
        public properties: TypeformMap,
        public validations: TypeformMap,
        public attachment: TypeformMap|null,
        public layout: TypeformMap|null,
    ) {
        this.initProperties(this.properties)
        this.initValidations(this.validations)
    }

    get description(): string {
        return this.properties.description as string
    }

    set description(description: string) {
        this.properties.description = description
    }

    get type(): string {
        return this._type
    }

    set type(type: string) {
        this._type = type
        this.initProperties()
        this.initValidations()
    }

    initProperties(properties: TypeformMap = {}): void {
        const propertyDefinitions = {
            allow_multiple_selection: { types: ['multiple_choice', 'picture_choice'], default: false },
            allow_other_choice: { types: ['multiple_choice', 'picture_choice'], default: false },
            alphabetical_order: { types: ['dropdown'], default: false },
            button_text: { types: ['group', 'payment', 'statement'], default: '' },
            choices: { types: ['ranking', 'dropdown', 'multiple_choice', 'picture_choice'], default: [] },
            currency: { types: ['payment'], default: 'EUR' },
            default_country_code: { types: ['phone_number'], default: 'nl' },
            description: { default: '' },
            fields: { types: ['group', 'matrix'], default: [] },
            hide_marks: { types: ['statement'], default: false },
            labels: { types: ['opinion_scale'], default: { left: '', center: '', right: '' } },
            price: { types: ['payment'], default: { type: 'variable', value: 'price' } },
            randomize: { types: ['multiple_choice', 'picture_choice', 'dropdown'], default: false },
            separator: { types: ['date'], default: '-' },
            shape: { types: ['rating'], default: 'star' },
            show_button: { types: ['group', 'payment'], default: false },
            show_labels: { types: ['picture_choice'], default: false },
            start_at_one: { types: ['opinion_scale'], default: false },
            steps: { types: ['opinion_scale', 'rating'], default: 10 },
            structure: { types: ['date'], default: 'DDMMYYYY' },
        }

        for (const property of Object.keys(propertyDefinitions)) {
            const propertyDefinition = propertyDefinitions[property]

            const types = 'types' in propertyDefinition ? propertyDefinition.types : []
            if (types.length > 0 && !types.includes(this.type)) {
                continue
            }

            const currentValue = property in this.properties ? this.properties[property] : null
            const defaultValue = 'default' in propertyDefinition ? propertyDefinition.default : null

            properties[property] = currentValue ?? defaultValue
        }

        this.properties = properties
    }

    initValidations(validations: TypeformMap = {}): void {
        const validationDefinitions = {
            max_length: { types: ['long_text', 'short_text'] },
            max_selection: { types: ['multiple_choice', 'picture_choice'] },
            max_value: { types: ['number'] },
            min_selection: { types: ['multiple_choice', 'picture_choice'] },
            min_value: { types: ['number'] },
            required: { types: ['matrix', 'ranking', 'date', 'dropdown', 'email', 'file_upload', 'legal', 'long_text', 'multiple_choice', 'number', 'opinion_scale', 'payment', 'picture_choice', 'rating', 'short_text', 'website', 'phone_number', 'yes_no'], default: false },
        }

        for (const validation of Object.keys(validationDefinitions)) {
            const validationDefinition = validationDefinitions[validation]

            const types = 'types' in validationDefinition ? validationDefinition.types : []
            if (types.length > 0 && !types.includes(this.type)) {
                continue
            }

            const currentValue = validation in this.validations ? this.validations[validation] : null
            const defaultValue = 'default' in validationDefinition ? validationDefinition.default : null

            validations[validation] = currentValue ?? defaultValue
        }

        this.validations = validations
    }

    getAllowedReferenceOptions(form: TypeformForm, valueCallback: (type: string, value: string) => string, allowSelf: boolean = false) {
        const options = []

        // All hidden fields
        form.hidden.forEach(hidden => {
            options.push({
                label: hidden,
                value: valueCallback('hidden', hidden),
            })
        })

        // All fields before this field that can be used in conditions (ie. can have an answer)
        const thisFieldIndex = form.fields.findIndex(field => field.ref === this.ref)

        form.fields.forEach((field, fieldIndex) => {
            if (thisFieldIndex !== -1 && fieldIndex > thisFieldIndex) {
                return
            } else if (thisFieldIndex !== -1 && fieldIndex === thisFieldIndex && !allowSelf) {
                return
            }

            const ops = TypeformLogicJumpFlattenedCondition.getFieldOps(field)
            if (ops.length === 0) {
                return
            }

            options.push({
                label: field.title.replaceAll(TypeformReferenceRegExp, '_'),
                value: valueCallback('field', field.ref),
            })
        })

        return options
    }

    getAllowedReferences(form: TypeformForm, valueCallback: (type: string, value: string) => string, allowSelf: boolean = false) {
        return this.getAllowedReferenceOptions(form, valueCallback, allowSelf).map(allowedReference => allowedReference.value)
    }

    toRequestData(): TypeformMap {
        const requestData: TypeformMap = {
            id: this.id,
            ref: this.ref,
            title: this.title,
            type: this.type,
            properties: this.properties,
            validations: this.validations,
        }

        if (this.attachment) {
            requestData.attachment = this.attachment
        }

        if (this.layout) {
            requestData.layout = this.layout
        }

        return requestData
    }

    static fromData(data: any): TypeformField {
        if (data === null || typeof data === 'string' || data instanceof TypeformField) {
            return data
        }

        return new TypeformField(
            data.id,
            data.ref,
            data.title || '',
            data.type,
            data.properties || {},
            data.validations || {},
            data.attachment,
            data.layout,
        )
    }
}

export type TypeformFieldUnion = TypeformField|TypeformWelcomeScreen|TypeformThankYouScreen

export type TypeformFieldArrayUnion = TypeformField[]|TypeformWelcomeScreen[]|TypeformThankYouScreen[]
